import { useCallback, useEffect, useState } from "react";
import { Award, Lock, Play } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { Loader, PageHeader } from "@/components/common";
import { FormDialog } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import GameRunner from "@/components/learn/GameRunner";
import ParentPanel from "@/components/learn/ParentPanel";

const KBTN = "inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl px-5 font-display text-base font-extrabold";

function GameCard({ g, onPlay, tryIt }) {
  return (
    <button onClick={() => onPlay(g)} className="group flex min-h-[148px] flex-col items-start gap-2 rounded-3xl border-2 border-[rgba(var(--glow-kids),0.25)] bg-q-surface p-4 text-left transition-[transform,border-color] duration-200 hover:border-[rgb(var(--glow-kids))] motion-safe:hover:-translate-y-1" data-testid={`game-${g.id}`}>
      <span className="text-5xl transition-transform duration-300 motion-safe:group-hover:rotate-6 motion-safe:group-hover:scale-110">{g.icon}</span>
      <span className="font-display text-lg font-extrabold leading-tight text-q-text">{g.name}</span>
      <span className="text-xs font-bold text-q-muted">{tryIt ? `Try it · ${g.ageLabel}` : `Level ${g.level} · ⭐ ${g.stars}`}</span>
    </button>
  );
}

function Badges({ kid, onClose }) {
  return (
    <FormDialog open onClose={onClose} title={`${kid.kid.name}'s badges`} testid="badges-dialog">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{kid.badges.map((b) => (
        <div key={b.id} className={`rounded-2xl p-3 text-center ${b.earned ? "bg-[rgba(var(--glow-kids),0.18)]" : "bg-white/5 opacity-60"}`} data-testid={`badge-${b.id}`}>
          <p className={`text-3xl ${b.earned ? "" : "grayscale"}`}>{b.earned ? b.icon : "🔒"}</p><p className="font-bold text-q-text">{b.name}</p>{!b.earned && <p className="text-xs text-q-muted">{b.hint}</p>}
        </div>
      ))}</div>
    </FormDialog>
  );
}

function AgePick({ d, onPick }) {
  return (
    <div className="space-y-3 rounded-3xl bg-q-surface p-6 text-center" data-testid="learn-session-age">
      <p className="font-display text-2xl font-extrabold text-q-text">Which games do you want to play?</p>
      <div className="flex flex-wrap justify-center gap-2">{Object.entries(d.ages).map(([id, l]) => <button key={id} className={`${KBTN} bg-[rgb(var(--glow-kids))] text-white`} onClick={() => onPick(id)} data-testid={`session-age-${id}`}>{l}</button>)}</div>
    </div>
  );
}

function KidLearn({ d, onPlay }) {
  const k = d.kids[0];
  const [subj, setSubj] = useState("all");
  const [badges, setBadges] = useState(false);
  const [pickAge, setPickAge] = useState(null);
  const banner = d.lapsed && <p className="rounded-2xl bg-[rgba(var(--glow-kids),0.15)] p-3 text-center font-semibold text-q-text" data-testid="learn-lapsed-banner">{d.lapsed_msg}</p>;
  if (k.session_age_pick && !pickAge) return <div className="space-y-5">{banner}<AgePick d={d} onPick={setPickAge} /></div>;
  if (!k.play_age && !k.session_age_pick) return <div className="space-y-5">{banner}<p className="rounded-3xl bg-q-surface p-8 text-center font-display text-2xl font-extrabold text-q-text" data-testid="learn-setup-needed">Ask a grown-up to set up Learn & Play</p></div>;
  const all = k.session_age_pick ? d.catalog.filter((g) => g.age === pickAge && k.settings.subjects[g.subject]).map((g) => ({ ...g, level: 1, stars: 0 })) : k.games;
  const subjects = [...new Set(all.map((g) => g.subject))];
  const games = all.filter((g) => subj === "all" || g.subject === subj);
  return (
    <div className="space-y-5">
      {banner}
      {k.locked && <p className="flex items-center justify-center gap-2 rounded-2xl bg-q-raised p-4 text-center font-display text-lg font-extrabold text-q-text" data-testid="learn-locked-banner"><Lock className="h-5 w-5" /> {d.lock_msg}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full bg-q-raised px-4 py-2 font-display text-lg font-extrabold text-q-text" data-testid="learn-stars">⭐ {k.stars}</span>
        <span className="rounded-full bg-q-raised px-4 py-2 font-display text-lg font-extrabold text-q-text" data-testid="learn-streak">🔥 {k.streak}-day streak</span>
        <button className={`${KBTN} bg-q-raised text-q-text`} onClick={() => setBadges(true)} data-testid="learn-badges-btn"><Award className="h-5 w-5" /> Badges {k.badges.filter((b) => b.earned).length}</button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">{["all", ...subjects].map((s) => <button key={s} className={`${KBTN} shrink-0 ${subj === s ? "bg-[rgb(var(--glow-kids))] text-white" : "bg-q-raised text-q-text"}`} onClick={() => setSubj(s)} data-testid={`learn-subject-${s}`}>{s === "all" ? "All" : d.subjects[s]}</button>)}</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="learn-games">{games.map((g) => <GameCard key={g.id} g={g} onPlay={onPlay} />)}</div>
      {badges && <Badges kid={k} onClose={() => setBadges(false)} />}
    </div>
  );
}

function AdultLearn({ hub, d, onPlay, refreshKey }) {
  const [sel, setSel] = useState(d.kids[0]?.kid.id || "try");
  const [tryAge, setTryAge] = useState("early");
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {d.kids.map((k) => <button key={k.kid.id} className={`q-btn min-h-[44px] ${sel === k.kid.id ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setSel(k.kid.id)} data-testid={`learn-kid-${k.kid.name.toLowerCase()}`}>{k.kid.name}</button>)}
        <button className={`q-btn min-h-[44px] ${sel === "try" ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setSel("try")} data-testid="learn-try-tab"><Play className="h-4 w-4" /> Try it</button>
      </div>
      {sel === "try" ? (
        <div className="space-y-3">
          <p className="text-sm text-q-muted">Play any game yourself. Nothing is saved and there are no rewards.</p>
          <div className="flex flex-wrap gap-2">{Object.entries(d.ages).map(([id, l]) => <button key={id} className={`q-btn min-h-[44px] ${tryAge === id ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setTryAge(id)} data-testid={`try-age-${id}`}>{l}</button>)}</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{d.catalog.filter((g) => g.age === tryAge).map((g) => <GameCard key={g.id} g={{ ...g, ageLabel: d.subjects[g.subject] }} tryIt onPlay={onPlay} />)}</div>
        </div>
      ) : <ParentPanel key={sel} hubId={hub.id} kidId={sel} meta={d} refreshKey={refreshKey} />}
    </div>
  );
}

export default function LearnPlay() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [game, setGame] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/learn`).then(({ data }) => { setD(data); setTick((t) => t + 1); }).catch((e) => toast.error(errMsg(e))), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "learn" || m.entity === "emergency.card", 20000);
  const close = useCallback(() => { setGame(null); load(); }, [load]);
  if (!d) return <Loader />;
  return (
    <div data-testid="learn-play-page">
      <PageHeader eyebrow="Kids Corner" title="Learn & Play" rgb="var(--glow-kids)" testid="learn-play-title" />
      {d.is_kid ? <KidLearn d={d} onPlay={setGame} /> : <AdultLearn hub={hub} d={d} onPlay={setGame} refreshKey={tick} />}
      {game && <GameRunner key={game.id} hubId={hub.id} game={game} kidMode={d.is_kid} lockMsg={d.lock_msg} onClose={close} />}
    </div>
  );
}
